import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  CURRENT_BRAND_STORAGE_KEY,
} from '../activeBrandChrome';

const BrandContext = createContext();

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

const initialBrandState = {
  name: null,
  type: null,
  id: null,
  logoUrl: null,
};

function toBrandState(brandInfo) {
  if (!brandInfo) return initialBrandState;
  return {
    ...initialBrandState,
    id: brandInfo.id || null,
    name: brandInfo.name || null,
    type: brandInfo.type || null,
    logoUrl: brandInfo.logoUrl || brandInfo.logo || null,
  };
}

/**
 * Synchronous read so the first render already carries the persisted Brand.
 * Brand-scoped route guards must not bounce a valid Brand (or a Support Mode
 * hard-navigation that just persisted one) to Brand Manager before hydration.
 */
function readPersistedBrandState() {
  try {
    const saved = localStorage.getItem(CURRENT_BRAND_STORAGE_KEY);
    return saved ? toBrandState(JSON.parse(saved)) : initialBrandState;
  } catch (error) {
    console.error('Error loading brand info:', error);
    return initialBrandState;
  }
}

export const BrandProvider = ({ children }) => {
  const [currentBrand, setCurrentBrand] = useState(readPersistedBrandState);

  const updateBrand = useCallback((brandInfo) => {
    setCurrentBrand(toBrandState(brandInfo));
  }, []);

  const clearBrand = useCallback(() => {
    setCurrentBrand(initialBrandState);
    try {
      localStorage.removeItem(CURRENT_BRAND_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (currentBrand.name && currentBrand.id) {
      localStorage.setItem(
        CURRENT_BRAND_STORAGE_KEY,
        JSON.stringify({
          id: currentBrand.id,
          name: currentBrand.name,
          type: currentBrand.type,
          logoUrl: currentBrand.logoUrl || null,
        }),
      );
    } else if (currentBrand.name === null) {
      localStorage.removeItem(CURRENT_BRAND_STORAGE_KEY);
    }
  }, [currentBrand]);

  return (
    <BrandContext.Provider value={{ currentBrand, updateBrand, clearBrand }}>
      {children}
    </BrandContext.Provider>
  );
};
